import { createEntityAdapter, EntityAdapter, EntityState, Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { ICO2Bottle, ICO2BottleVM } from 'app/models/co2-bottle';
import * as Action from './actions';
import * as fromUnits from '../units/reducer';
import * as fromLocation from '../location/reducer';
import * as fromPlaato2 from '../plaato2/reducer';
import * as fromTower from '../tower/reducer';
import * as fromBar from '../bar/reducer';
import { UnitsHelper } from 'app/services/units.helper';
import { ILocation } from 'app/models/location';
import { IPlaato2 } from 'app/models/plaato2';

export interface State extends EntityState<ICO2Bottle> {
  selectedId: string | null;
  error: string;
}

export function sortByOrder(a: ICO2Bottle, b: ICO2Bottle): number {
  const compare = a.order - b.order;
  return compare > 0 ? 1 : compare < 0 ? -1 : 0;
}

export function selectId(a: ICO2Bottle): string {
  return a.id ?? '';
}

export const adapter: EntityAdapter<ICO2Bottle> = createEntityAdapter<ICO2Bottle>({
  sortComparer: sortByOrder,
  selectId,
});

export const initialState: State = adapter.getInitialState({
  selectedId: null,
  error: '',
});

export const stateName = 'co2Bottle';

export const reducer = createReducer<State>(
  initialState,
  on(Action.clearAllItems, (state) => adapter.removeAll(state)),
  on(Action.loadItemsSuccess, (state, { items }) => adapter.setAll(items, state)),
  on(Action.updateItem, (state, { item }) => adapter.setOne(item, state)),
  on(
    Action.setItem,
    Action.setItemAndNavigate,
    Action.setItemFromListPage,
    (state, action) => Object.assign({ ...state, selectedId: action.id })),
);

const {
  selectAll,
  selectEntities: selectItemEntities,
} = adapter.getSelectors();

export { selectAll };

export const getSelectedId = (state: State) => state.selectedId;
const getState = createFeatureSelector<State>(stateName);
export const selectAllItems = createSelector(getState, selectAll);
export const selectAllEntities = createSelector(getState, selectItemEntities);
export const selectCurrentId = createSelector(getState, getSelectedId);
export const selectCurrentItem = createSelector(selectAllEntities, selectCurrentId, (entities, id) => {
  if (id) return entities[id];
  return null;
});
export const selectEntity = (id: string) => createSelector(selectAllEntities, entities => entities[id]);

/**
 * CO2 weights (size/amount/tareWeight) are stored in kg and displayed via units.kegWeight.
 */
export const getCO2BottlesWithUnit = createSelector(
  selectAllItems,
  fromUnits.getUnits,
  (bottles, units) => {
    if (!units) return bottles;
    const helper = new UnitsHelper();
    return bottles.map(item => ({
      ...item,
      size: item.size ? helper.toUnit(item.size, 'kg', units.kegWeight, 2) : 0,
      amount: item.amount ? helper.toUnit(item.amount, 'kg', units.kegWeight, 2) : 0,
      tareWeight: item.tareWeight ? helper.toUnit(item.tareWeight, 'kg', units.kegWeight, -1) : 0,
    } as ICO2Bottle));
  });

export const getCO2BottlesWithLocationAndPlaato2 = createSelector(
  getCO2BottlesWithUnit,
  fromLocation.selectAllEntities,
  fromPlaato2.selectAllEntities,
  (bottles: ICO2Bottle[], locations: Dictionary<ILocation>, plaato2s: Dictionary<IPlaato2>) => {
    return bottles.map(item => ({
      ...item,
      location: item.locationId ? locations[item.locationId] : undefined,
      plaato2: item.plaato2Id ? plaato2s[item.plaato2Id] : undefined,
    } as ICO2BottleVM));
  });

/** CO2 bottles placed at the location shown in location-detail (same
 *  pattern as keg `selectAllItemsFromCurrentLocation`). */
export const selectAllItemsFromCurrentLocation = createSelector(
  getCO2BottlesWithLocationAndPlaato2,
  fromLocation.selectCurrentId,
  (items, locationId) => items.filter(item => item.locationId === locationId));

/** CO2 bottles that belong to the tower shown in the detail view (tower-state selectedId). */
export const selectCO2BottlesBySelectedTowerId = () => createSelector(
  fromTower.selectCurrentId,
  getCO2BottlesWithLocationAndPlaato2,
  (towerId, items) => towerId ? items.filter(item => item.towerId === towerId) : []);

/** CO2 bottles that are NOT on the selected tower — for the "Add CO2" picker. */
export const selectCO2BottlesNotInSelectedTower = createSelector(
  fromTower.selectCurrentId,
  getCO2BottlesWithLocationAndPlaato2,
  (towerId, items) => items.filter(item => item.towerId !== towerId));

/**
 * CO2 bottles for Home, filtered by the bar's selected tower (same principle as
 * taps). Without a selected tower, all are shown.
 */
export const selectCO2BottlesFromSelectedTower = createSelector(
  fromBar.selectBar,
  getCO2BottlesWithLocationAndPlaato2,
  (bar, items) => bar?.selectedTowerId ? items.filter(item => item.towerId === bar.selectedTowerId) : items);
